import { apiUrl } from './config';

export interface PublicTemplateItem {
  id: string;
  name: string;
  slug: string;
  category: string;
  isPremium: boolean;
  thumbnail?: string | null;
  thumbnailLarge?: string | null;
  thumbnailMedium?: string | null;
  image?: string | null;
  html?: string | null;
  json?: any;
}

function versionedThumbnail(url: string | null | undefined): string | null {
  if (!url) return null;
  const clean = url.replace(/\?v=[0-9]+/, '');
  return `${clean}?v=10`;
}

export function cleanTemplateName(rawName: any): string {
  if (!rawName) return 'Untitled Template';
  let name = '';
  if (typeof rawName === 'string') {
    name = rawName;
  } else if (typeof rawName === 'object' && rawName !== null) {
    name = rawName.en || rawName.name || rawName.title || 'Untitled Template';
  } else {
    name = String(rawName);
  }

  // Strip '[object Object] - ' if present
  let cleaned = name.replace(/^\[object Object\]\s*-\s*/i, '').trim();

  // If name has a company/brand prefix like "Brand - Template Name", keep the meaningful template name
  if (cleaned.includes(' - ')) {
    const parts = cleaned.split(' - ');
    if (parts.length > 1 && parts[1]?.trim()) {
      cleaned = parts.slice(1).join(' - ').trim();
    }
  }

  return cleaned || 'Email Template';
}

export function slugifyText(text: string): string {
  if (!text) return '';
  return text
    .toLowerCase()
    .replace(/['"’]/g, '')
    .replace(/[^\w\s-]/g, ' ')
    .trim()
    .replace(/[\s_-]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

const API_ENDPOINT = apiUrl('/api/templates?limit=600&sort=updated');

// In-memory cache per worker / process
let inMemoryTemplates: PublicTemplateItem[] | null = null;
const templateBySlugOrId = new Map<string, PublicTemplateItem>();
const PROCESS_START_TIME = Date.now();

function getDiskCache(): PublicTemplateItem[] | null {
  if (typeof window !== 'undefined') return null;
  try {
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    const fs = require('fs');
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    const path = require('path');
    const cacheFile = path.join(process.cwd(), '.next', 'postbrix-templates-cache.json');
    if (fs.existsSync(cacheFile)) {
      const stats = fs.statSync(cacheFile);
      // Reuse disk cache up to 24 hours old
      if (Date.now() - stats.mtimeMs > 24 * 60 * 60 * 1000) {
        return null;
      }
      const raw = fs.readFileSync(cacheFile, 'utf8');
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed) && parsed.length >= 20) {
        return parsed;
      }
    }
  } catch {
    // ignore
  }
  return null;
}

function setDiskCache(templates: PublicTemplateItem[]): void {
  if (typeof window !== 'undefined') return;
  try {
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    const fs = require('fs');
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    const path = require('path');
    const cacheDir = path.join(process.cwd(), '.next');
    if (!fs.existsSync(cacheDir)) {
      fs.mkdirSync(cacheDir, { recursive: true });
    }
    const cacheFile = path.join(cacheDir, 'postbrix-templates-cache.json');
    fs.writeFileSync(cacheFile, JSON.stringify(templates), 'utf8');
  } catch {
    // ignore
  }
}

function populateMemoryCache(templates: PublicTemplateItem[]) {
  inMemoryTemplates = templates;
  for (const t of templates) {
    if (t.slug) templateBySlugOrId.set(t.slug, t);
    if (t.id) templateBySlugOrId.set(t.id, t);
    if (t.name) {
      const nameSlug = slugifyText(t.name);
      if (nameSlug && !templateBySlugOrId.has(nameSlug)) {
        templateBySlugOrId.set(nameSlug, t);
      }
    }
  }
}

export async function fetchAllPublicTemplates(): Promise<PublicTemplateItem[]> {
  // 1. Check in-memory cache
  if (inMemoryTemplates && inMemoryTemplates.length > 0) {
    return inMemoryTemplates;
  }

  // 2. Check disk cache across build workers
  const diskCache = getDiskCache();
  if (diskCache && diskCache.length > 0) {
    populateMemoryCache(diskCache);
    return diskCache;
  }

  // 3. Fetch from API
  try {
    const res = await fetch(API_ENDPOINT, {
      cache: 'no-store',
      signal: AbortSignal.timeout(15000),
    });
    if (res.ok) {
      const data = await res.json();
      const rawList = Array.isArray(data) ? data : data.templates || [];
      const validList = rawList.filter((item: any) => {
        const name = String(item.name || item.title || '');
        return name && !name.startsWith('Untitled') && name !== 'Loading...';
      });

      if (validList.length > 0) {
        const mapped: PublicTemplateItem[] = validList.map((item: any) => ({
          id: String(item.id || item.slug),
          name: cleanTemplateName(item.name || item.title),
          slug: item.slug || item.id,
          category: item.category || 'General',
          isPremium: Boolean(item.isPremium),
          thumbnail: versionedThumbnail(item.thumbnail),
          thumbnailLarge: versionedThumbnail(item.thumbnailLarge),
          thumbnailMedium: versionedThumbnail(item.thumbnailMedium),
          image: versionedThumbnail(item.thumbnailLarge || item.thumbnailMedium || item.thumbnail),
          html: item.html || null,
          json: item.json || null,
        }));

        // Pre-fetch rendered HTML for the few templates that only have JSON/AST
        const missingHtml = mapped.filter((t) => !t.html && t.id);
        if (missingHtml.length > 0) {
          await Promise.allSettled(
            missingHtml.map(async (t) => {
              try {
                const renderRes = await fetch(
                  apiUrl(`/api/render-email/${t.id}?preview=true`),
                  { signal: AbortSignal.timeout(4000) }
                );
                if (renderRes.ok) {
                  const text = await renderRes.text();
                  if (text && text.length > 50 && !text.includes('ASTValidationError')) {
                    t.html = text;
                  }
                }
              } catch {
                // ignore
              }
            })
          );
        }

        populateMemoryCache(mapped);
        setDiskCache(mapped);
        return mapped;
      }
    }
  } catch {
    // ignore
  }

  // Return empty array if API is unreachable (never return fake dummy templates)
  return [];
}

export async function fetchSingleTemplate(slug: string): Promise<PublicTemplateItem | null> {
  const normalizedSlug = slug.trim().toLowerCase();

  // 1. Check in-memory cache directly
  if (templateBySlugOrId.has(slug)) {
    const item = templateBySlugOrId.get(slug)!;
    if (item.html) return item;
  }
  if (templateBySlugOrId.has(normalizedSlug)) {
    const item = templateBySlugOrId.get(normalizedSlug)!;
    if (item.html) return item;
  }

  // 2. If memory cache empty, populate via fetchAllPublicTemplates
  if (!inMemoryTemplates || inMemoryTemplates.length === 0) {
    await fetchAllPublicTemplates();
    if (templateBySlugOrId.has(slug)) {
      const item = templateBySlugOrId.get(slug)!;
      if (item.html) return item;
    }
    if (templateBySlugOrId.has(normalizedSlug)) {
      const item = templateBySlugOrId.get(normalizedSlug)!;
      if (item.html) return item;
    }
  }

  // 3. Fallback fuzzy / variant search in inMemoryTemplates
  if (inMemoryTemplates && inMemoryTemplates.length > 0) {
    const match = inMemoryTemplates.find((t) => {
      if (t.slug && t.slug.toLowerCase() === normalizedSlug) return true;
      if (t.id && t.id.toLowerCase() === normalizedSlug) return true;
      if (t.name && slugifyText(t.name) === normalizedSlug) return true;
      if (t.slug && (normalizedSlug.startsWith(t.slug) || t.slug.startsWith(normalizedSlug))) return true;
      return false;
    });

    if (match) {
      templateBySlugOrId.set(slug, match);
      if (match.html) return match;
    }
  }

  // 4. Query single template directly from API
  try {
    const res = await fetch(apiUrl(`/api/templates/${encodeURIComponent(slug)}`), {
      next: { revalidate: 3600 },
      signal: AbortSignal.timeout(6000),
    });
    if (res.ok) {
      const item = await res.json();
      if (item && (item.id || item.slug)) {
        let html = item.html || null;

        if (!html && item.id) {
          try {
            const renderRes = await fetch(
              apiUrl(`/api/render-email/${item.id}?preview=true`),
              { signal: AbortSignal.timeout(3000) }
            );
            if (renderRes.ok) {
              const renderText = await renderRes.text();
              if (renderText && renderText.length > 50 && !renderText.includes('ASTValidationError')) {
                html = renderText;
              }
            }
          } catch {
            // ignore
          }
        }

        const resolved: PublicTemplateItem = {
          id: String(item.id || item.slug),
          name: cleanTemplateName(item.name || item.title),
          slug: item.slug || item.id,
          category: item.category || 'General',
          isPremium: Boolean(item.isPremium),
          thumbnail: versionedThumbnail(item.thumbnail),
          thumbnailLarge: versionedThumbnail(item.thumbnailLarge),
          thumbnailMedium: versionedThumbnail(item.thumbnailMedium),
          image: versionedThumbnail(item.thumbnailLarge || item.thumbnailMedium || item.thumbnail),
          html: html || null,
          json: item.json || null,
        };

        templateBySlugOrId.set(slug, resolved);
        if (resolved.slug) templateBySlugOrId.set(resolved.slug, resolved);
        if (resolved.id) templateBySlugOrId.set(resolved.id, resolved);
        return resolved;
      }
    }
  } catch {
    // ignore
  }

  // Return null if not found in API (never return dummy templates)
  return null;
}

export async function getAllTemplateSlugs(): Promise<{ slug: string }[]> {
  try {
    const templates = await fetchAllPublicTemplates();
    const slugs = new Set<string>();

    templates.forEach((t) => {
      if (t.slug) slugs.add(t.slug);
      if (t.id) slugs.add(t.id);
      if (t.name) {
        const nameSlug = slugifyText(t.name);
        if (nameSlug) slugs.add(nameSlug);
      }
    });

    const result = Array.from(slugs).map((slug) => ({ slug }));
    if (result.length === 0) {
      return [{ slug: 'my-awesome-layout' }];
    }
    return result;
  } catch {
    return [{ slug: 'my-awesome-layout' }];
  }
}
